import { createTheme, responsiveFontSizes, type Theme } from '@mui/material/styles';
import { navy, solar, statusFor } from './palette';
import { typography } from './typography';
import { components } from './components';

export { navy, solar, statusFor, leadStatusTone } from './palette';
export type ColorMode = 'light' | 'dark';

/**
 * Navy carries the structure in both modes; solar orange is the accent.
 *
 * MUI mapping: `primary` is navy and drives most controls, `secondary` is the
 * orange used only on conversion actions (hero CTA, header CTA, form submit)
 * so it never becomes ambient decoration.
 */
export const buildTheme = (mode: ColorMode): Theme => {
  const dark = mode === 'dark';
  const status = statusFor(dark);

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: dark ? navy[300] : navy[700],
        light: dark ? navy[200] : navy[600],
        dark: dark ? navy[400] : navy[800],
        contrastText: dark ? navy[950] : '#ffffff',
      },
      secondary: {
        main: solar[400],
        light: solar[300],
        dark: solar[500],
        // Navy ink on orange clears AA comfortably; white on orange does not.
        contrastText: navy[900],
      },
      background: {
        default: dark ? navy[950] : navy[50],
        paper: dark ? navy[900] : '#ffffff',
      },
      text: {
        primary: dark ? navy[100] : navy[900],
        secondary: dark ? navy[300] : navy[600],
        // Still AA against the paper surface: `disabled` gets used for real
        // secondary content, not only for inactive controls.
        disabled: dark ? navy[400] : navy[500],
      },
      divider: dark ? 'rgba(163,190,219,0.16)' : navy[200],
      grey: navy,
      success: status.success,
      warning: status.warning,
      error: status.error,
      info: status.info,
      action: {
        hover: dark ? 'rgba(163,190,219,0.08)' : 'rgba(14,42,92,0.05)',
        selected: dark ? 'rgba(163,190,219,0.14)' : 'rgba(14,42,92,0.09)',
      },
    },
    shape: { borderRadius: 10 },
    typography,
    components: components(mode),
  });

  return responsiveFontSizes(theme);
};
